import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Card, CardContent, CardHeader } from '@packages/shared';
import { AvailabilityInfo } from '@packages/modules/src/AvailabilityInfo/AvailabilityInfo';
import { useIntl } from 'react-intl';
import { DeliveryInfoCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/DeliveryInfoCardFragmentFragmentDoc';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { CarrierDeliveryInfo } from '../CarrierDeliveryInfo';
import { ExpressServiceInfo } from '../ExpressServiceInfo';
import { messages } from './messages';
import { Scarcity } from '../Scarcity';
import { AdditionalServices } from '../AdditionalInfo/AdditionalServices';

/* GraphQL */ `
  fragment DeliveryInfoCardFragment on Product {
    availability {
      message
      state
      hasExpressService
      ...ScarcityFragment
    }
    delivery {
      deliverySize
      ...CarrierDeliveryInfoFragment
    }
    additionalInfo {
      additionalServices {
        ...AdditionalServicesFragment
      }
    }
  }
`;

type DeliveryProps = {
  data: FragmentType<typeof DeliveryInfoCardFragmentFragmentDoc>;
};

/**
 * This component is a wrapper to show all delivery infos for a given product.
 * Its subcomponents are:
 * <ul>
 *  <li>`AvailabilityInfo`</li>
 *  <li>`CarrierDeliveryInfo`</li>
 *  <li>`ExpressServiceInfo`</li>
 * </ul>
 */
export const DeliveryInfoCard = ({ data }: DeliveryProps) => {
  const product = getFragmentData(DeliveryInfoCardFragmentFragmentDoc, data);
  const {
    product: {
      ecoFriendlyDelivery: { enabled, serviceLayerID },
    },
  } = useConfig();

  const {
    delivery: { deliverySize },
    availability,
    additionalInfo,
  } = product;

  const { formatMessage } = useIntl();
  return (
    <Card>
      <CardHeader
        title={formatMessage(messages.deliverycardHeadline)}
        sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
      />
      <CardContent>
        <div className="flex flex-col items-start gap-1">
          <Scarcity data={availability} />
          <div id="dy_info_verfuegbarkeit_dv" />
          <AvailabilityInfo
            availabilityMessage={availability.message}
            availabilityState={availability.state}
            serviceLayer={enabled ? <ServiceLayer serviceLayerId={serviceLayerID} /> : undefined}
          />
          {/* there is no carrier delivery for normal sized products (size === "DEFAULT"), they are sent by standard delivery */}
          {deliverySize !== 'DEFAULT' && <CarrierDeliveryInfo data={product.delivery} />}
          {availability.hasExpressService && (
            <>
              <Divider className="w-full" />
              <ExpressServiceInfo />
            </>
          )}
          <AdditionalServices
            data={additionalInfo?.additionalServices}
            dialogTitle={formatMessage(messages.deliverycardHeadline)}
            divider={<Divider className="my-0.5 w-full" />}
          />
        </div>
      </CardContent>
    </Card>
  );
};
